import React, { useState, useRef, useEffect } from 'react';
import useStore from '../store/useStore';
import { dashboardsAPI, metricsAPI } from '../services/api';

const DashboardSelector = () => {
  const dashboards = useStore((state) => state.dashboards);
  const currentDashboard = useStore((state) => state.currentDashboard);
  const currentCompany = useStore((state) => state.currentCompany);
  const setCurrentDashboard = useStore((state) => state.setCurrentDashboard);
  const setMetrics = useStore((state) => state.setMetrics);
  const setDashboardLastUpdate = useStore((state) => state.setDashboardLastUpdate);
  const dashboardLastUpdates = useStore((state) => state.dashboardLastUpdates);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDashboardChange = async (dashboard) => {
    setIsOpen(false);

    // Skip if already on this dashboard
    if (currentDashboard?.id === dashboard.id) {
      return;
    }

    try {
      // Load the full dashboard data including widgets
      const dashboardResponse = await dashboardsAPI.getById(dashboard.id);
      if (dashboardResponse.success) {
        setCurrentDashboard(dashboardResponse.data);
      }

      // Load metrics for this company (metrics are company-scoped, not dashboard-scoped)
      if (currentCompany && currentCompany.id) {
        const metricsResponse = await metricsAPI.getLatestAll(currentCompany.id);
        if (metricsResponse.success) {
          const metricsMap = {};
          let mostRecentTimestamp = null;

          metricsResponse.data.forEach((metric) => {
            metricsMap[metric.metric_key] = metric;

            // Track the most recent timestamp from all metrics
            if (metric.timestamp) {
              const metricTime = new Date(metric.timestamp);
              if (!mostRecentTimestamp || metricTime > mostRecentTimestamp) {
                mostRecentTimestamp = metricTime;
              }
            }
          });

          setMetrics(metricsMap);

          // Only set initial timestamp for the dashboard if it doesn't already have one
          // This prevents overwriting timestamps when switching dashboards
          // Real-time updates will come from WebSocket
          if (mostRecentTimestamp && dashboard.id) {
            const currentTimestamp = dashboardLastUpdates[dashboard.id];
            if (!currentTimestamp) {
              setDashboardLastUpdate(dashboard.id, mostRecentTimestamp);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error switching dashboard:', error);
    }
  };

  // Don't show selector if there are no dashboards
  if (!dashboards || dashboards.length === 0) {
    return null;
  }

  return (
    <div className="dashboard-selector" ref={dropdownRef}>
      <button
        className="dashboard-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="selector-icon">📊</span>
        <span className="dashboard-name">
          {currentDashboard?.name || 'Select Dashboard'}
        </span>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="dashboard-dropdown">
          {dashboards.map((dashboard) => (
            <button
              key={dashboard.id}
              className={`dashboard-option ${
                currentDashboard?.id === dashboard.id ? 'active' : ''
              }`}
              onClick={() => handleDashboardChange(dashboard)}
            >
              <span className="dashboard-option-name">{dashboard.name}</span>
              {currentDashboard?.id === dashboard.id && (
                <span className="checkmark">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardSelector;
